import api from './api';
import type { AnnouncementCategory } from '../constants/announcementCategories';

/** 公開 API が返す1件（表示言語に合わせた title / body） */
export interface PublicAnnouncement {
  id: number;
  category: AnnouncementCategory;
  title: string;
  body: string;
  linkUrl: string | null;
  publishedAt: string | null;
  lang: 'ja' | 'en';
}

/** 管理 API が返す1件（全言語の生データ） */
export interface AdminAnnouncement {
  id: number;
  category: AnnouncementCategory;
  titleJa: string;
  bodyJa: string;
  titleEn: string | null;
  bodyEn: string | null;
  linkUrl: string | null;
  isPublished: boolean;
  publishedAt: string | null;
  emailSentAt: string | null;
  emailSentCount: number;
  createdBy: number | null;
  createdAt: string;
  updatedAt: string;
  creator?: { id: number; username: string } | null;
}

export interface AnnouncementInput {
  category: AnnouncementCategory;
  titleJa: string;
  bodyJa: string;
  titleEn?: string | null;
  bodyEn?: string | null;
  linkUrl?: string | null;
}

export interface NotificationSetting {
  category: AnnouncementCategory;
  emailEnabled: boolean;
}

export const announcementService = {
  async listPublic(params?: { lang?: string; category?: string; limit?: number }): Promise<PublicAnnouncement[]> {
    const response = await api.get('/announcements', { params });
    return response.data.data as PublicAnnouncement[];
  },

  async getMySettings(): Promise<NotificationSetting[]> {
    const response = await api.get('/announcements/me/settings');
    return response.data.data as NotificationSetting[];
  },

  async updateMySettings(settings: NotificationSetting[]): Promise<NotificationSetting[]> {
    const response = await api.put('/announcements/me/settings', { settings });
    return response.data.data as NotificationSetting[];
  },

  async listAdmin(params?: { page?: number; limit?: number }): Promise<{
    data: AdminAnnouncement[];
    pagination: { page: number; limit: number; total: number; totalPages: number };
  }> {
    const response = await api.get('/announcements/admin', { params });
    return response.data;
  },

  async create(input: AnnouncementInput): Promise<AdminAnnouncement> {
    const response = await api.post('/announcements/admin', input);
    return response.data.data as AdminAnnouncement;
  },

  async update(id: number, input: Partial<AnnouncementInput>): Promise<AdminAnnouncement> {
    const response = await api.put(`/announcements/admin/${id}`, input);
    return response.data.data as AdminAnnouncement;
  },

  async setPublished(id: number, published: boolean): Promise<AdminAnnouncement> {
    const response = await api.post(`/announcements/admin/${id}/publish`, { published });
    return response.data.data as AdminAnnouncement;
  },

  async recipients(id: number): Promise<{ total: number; ja: number; en: number }> {
    const response = await api.get(`/announcements/admin/${id}/recipients`);
    return response.data.data;
  },

  async sendEmail(id: number): Promise<{ sent: number; failed: number; recipients: number; announcement: AdminAnnouncement }> {
    const response = await api.post(`/announcements/admin/${id}/send-email`, {});
    return response.data.data;
  },

  async delete(id: number): Promise<void> {
    await api.delete(`/announcements/admin/${id}`);
  },
};
